import {useState} from "react";
import {
    FaRegThumbsUp,
    FaRegThumbsDown,
    FaRegCopy,
    FaCheck,
} from "react-icons/fa6";
import React from "react";
import {useTranslation} from "react-i18next";
import Mixpanel from "../utils/mixpanel";
interface TranscriptionDisplayProps {
    transcription: string;
    onSubmitFeedback: (
        feedback: string,
        corrected_transcription: string
    ) => void;
}

const TranscriptionDisplay = ({
    transcription,
    onSubmitFeedback,
}: TranscriptionDisplayProps) => {
    const [feedback, setFeedback] = useState(null);
    const [transcriptionText, setTranscriptionText] = useState(transcription);
    const [correctedText, setCorrectedText] = useState("");
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [copied, setCopied] = useState(false);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const {t} = useTranslation("global");

    const handleFeedback = (isPositive) => {
        setFeedback(isPositive);
        // Reset correctedText to original transcription if switching to positive feedback
        if (isPositive) {
            setCorrectedText(transcription);
        }
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(transcriptionText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            Mixpanel.track("Copied transcription", {
                transcription: transcriptionText,
            });
        } catch (error) {
            console.error("Failed to copy text:", error);
            Mixpanel.track("Failed to copy transcription", {
                error: error.message,
            });
        }
    };

    return (
        <div className="w-full bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-semibold text-indigo-700 mb-4">
                {t("transcription.title")}
            </h2>
            <p className="w-full text-gray-600 text-sm mb-4">
                {t("feedback.explanation")}
            </p>

            <div className="flex flex-row gap-4">
                <div className="w-full relative">
                    <div className="flex flex-col gap-2">
                        <div className="flex flex-row justify-between items-center">
                            <h3 className="text-lg font-semibold text-indigo-700">
                                {t("transcription.original_text")}
                            </h3>
                            <button
                                onClick={handleCopy}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all duration-300 ${
                                    copied
                                        ? "bg-green-500 text-white shadow-lg"
                                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                                }`}
                                title={
                                    copied
                                        ? t("transcription.copied_text")
                                        : t("transcription.copy_text")
                                }
                            >
                                {copied ? (
                                    <>
                                        <FaCheck className="h-4 w-4" />
                                        <span className="text-sm font-medium">
                                            {t("transcription.copied_text")}
                                        </span>
                                    </>
                                ) : (
                                    <FaRegCopy className="h-4 w-4" />
                                )}
                            </button>
                        </div>
                        <textarea
                            className="w-full min-h-[260px] p-4 bg-white/50 border border-gray-200 rounded-xl mb-6 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none resize-vertical"
                            value={transcriptionText}
                            placeholder={t(
                                "transcription.transcription_placeholder"
                            )}
                            disabled={true}
                        />
                    </div>
                </div>
                {feedback === false && (
                    <div className="w-full">
                        <h3 className="text-lg font-semibold text-indigo-700 mb-2">
                            {t("transcription.corrected_text")}
                        </h3>
                        <textarea
                            ref={textareaRef}
                            className="w-full min-h-[260px] p-4 bg-white/50 border border-gray-200 rounded-xl mb-6 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none resize-vertical"
                            value={correctedText}
                            onChange={(e) => setCorrectedText(e.target.value)}
                            placeholder={t(
                                "transcription.correction_placeholder"
                            )}
                            disabled={feedback === true || feedback === null}
                        />
                    </div>
                )}
            </div>

            {
                <div className="flex items-center justify-between gap-4 px-2">
                    {!buttonDisabled ? (
                        <div className="flex items-center gap-4">
                            <span className="text-[16px] font-semibold text-indigo-700">
                                {t("transcription.feedback_question")}
                            </span>
                            <button
                                onClick={() => handleFeedback(true)}
                                className={`p-2 rounded-full hover:bg-gray-100 ${
                                    feedback === true
                                        ? "text-green-500"
                                        : "text-gray-400"
                                }`}
                            >
                                <FaRegThumbsUp className="h-5 w-5" />
                            </button>
                            <button
                                onClick={() => handleFeedback(false)}
                                className={`p-2 rounded-full hover:bg-gray-100 ${
                                    feedback === false
                                        ? "text-red-500"
                                        : "text-gray-400"
                                }`}
                            >
                                <FaRegThumbsDown className="h-5 w-5" />
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-4">
                            <span className="text-[16px] font-semibold text-indigo-700">
                                {t("feedback.thanks")}
                            </span>
                        </div>
                    )}

                    {feedback === false && !buttonDisabled ? (
                        <button
                            onClick={() => {
                                onSubmitFeedback("negative", correctedText);
                                setButtonDisabled(true);
                                Mixpanel.track("Feedback submitted", {
                                    feedback: "negative",
                                    corrected_transcription: correctedText,
                                });
                            }}
                            className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full 
                                     font-semibold shadow-lg hover:shadow-xl transition-all duration-300
                                     hover:scale-105 active:scale-95 text-sm"
                        >
                            {t("transcription.button_send_feedback_negative")}
                        </button>
                    ) : (
                        feedback === true &&
                        !buttonDisabled && (
                            <button
                                onClick={() => {
                                    onSubmitFeedback("positive", correctedText);
                                    Mixpanel.track("Feedback submitted", {
                                        feedback: "positive",
                                        transcription: correctedText,
                                    });
                                    setButtonDisabled(true);
                                }}
                                className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full 
                                         font-semibold shadow-lg hover:shadow-xl transition-all duration-300
                                         hover:scale-105 active:scale-95 text-sm"
                            >
                                {t(
                                    "transcription.button_send_feedback_positive"
                                )}
                            </button>
                        )
                    )}
                </div>
            }
        </div>
    );
};

export default TranscriptionDisplay;
